import { Box, Typography } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { FigureTypography } from "src/components/Typography/TitleTypography";
import OverviewMonitoringWrapper from "src/components/Wrapper/OverviewMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserAnalysisChartSelector, useUserAnalysisSelector } from "src/context/monitoring/userAnalysisContext";

const getOption = () => ({
  chart: {
    height: 260,
  },
  xAxis: {
    type: "datetime",
    labels: {
      enabled: false,
    },
  },
  yAxis: {
    tickAmount: 6,
    labels: {
      enabled: false,
    },
  },
  tooltip: {
    useHTML: true,
    style: {
      display: "block",
    },
    formatter: function () {
      const points = this.points;
      let data = "";
      for (let config of points) {
        const color = config.color;
        data += `<br /><div class="scoring-chart-tooltip-container">\
        <div class="scoring-chart-tooltip-circle" style="background:${color}"></div>\
        <span>${config.series.name}</span>:&nbsp;<b>${config.y}</b></div>`;
      }
      const id = this.points[0]["point"].id;
      const times = id.split("-");
      let time1 = times[0].length === 0 ? null : Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[0]));
      let time2 = Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[1]));
      let result = time1 ? `<div>From: ${time1}</div>` : "";
      result += `<div>To: ${time2}</div>`;
      data = `<div class="scoring-chart-tooltip-x">${result}</div>` + data;
      return data;
    },
  },
});

export default function NOURTransactionTypesOverview() {
  const { pool } = useParams();

  const { fetchNOURTransactionTypes } = useUserAnalysisSelector((state) => state.fetch);
  const nourTransactionTypes = useUserAnalysisChartSelector(MONITORING.ANALYSIS.nourTransactionTypes, pool);

  useEffect(() => {
    fetchNOURTransactionTypes(pool, { number: 90, interval: 8 }, { active: 2, isCheckMetadata: false });
  }, [pool]);

  const { series, isLoading } = useMemo(() => {
    if (nourTransactionTypes) {
      return {
        series: [
          { name: "Borrow", data: [].concat(nourTransactionTypes.borrow) },
          { name: "Deposit", data: [].concat(nourTransactionTypes.deposit) },
          { name: "Repay", data: [].concat(nourTransactionTypes.repay) },
          { name: "Withdraw", data: [].concat(nourTransactionTypes.withdraw) },
        ],
        isLoading: false,
      };
    }
    return {
      series: [
        { name: "Borrow", data: [] },
        { name: "Deposit", data: [] },
        { name: "Repay", data: [] },
        { name: "Withdraw", data: [] },
      ],
      isLoading: true,
    };
  }, [nourTransactionTypes]);

  const { lastDeposit, lastBorrow, lastRepay, lastWithdraw, lastDate } = useMemo(() => {
    if (nourTransactionTypes) {
      const date = Highcharts.dateFormat("%b %e, %Y, %k:%M", nourTransactionTypes.deposit.slice(-1)[0]["x"]);
      return {
        lastDeposit: nourTransactionTypes.deposit.slice(-1)[0]["y"],
        lastBorrow: nourTransactionTypes.borrow.slice(-1)[0]["y"],
        lastRepay: nourTransactionTypes.repay.slice(-1)[0]["y"],
        lastWithdraw: nourTransactionTypes.withdraw.slice(-1)[0]["y"],
        lastDate: date,
      };
    }
    return {
      lastDeposit: 0,
      lastBorrow: 0,
      lastDepositOnlyUser: 0,
      lastWithdraw: 0,
      lastDate: "",
    };
  }, [nourTransactionTypes]);

  return (
    <OverviewMonitoringWrapper
      isLoading={isLoading}
      title="Number Of Users Regarding Transaction Types"
      overview="The chart shows the daily number of users who deposit, borrow, withdraw, or repay their assets in a lending pool."
      to={`/user-analysis/number-of-users-regarding-transaction-types/${pool}`}
    >
      <Box>
        <Box display="flex" flexWrap="wrap">
          <FigureTypography value={lastDeposit} title="Deposit" style={{ marginRight: "1rem" }} />
          <FigureTypography value={lastBorrow} title="Borrow" index={1} style={{ marginRight: "1rem" }} />
          <FigureTypography value={lastRepay} title="Repay" index={2} style={{ marginRight: "1rem" }} />
          <FigureTypography value={lastWithdraw} title="Withdraw" index={3} />
        </Box>
        <Typography>{lastDate}</Typography>
      </Box>
      <LineChart series={series} option={getOption()} />
    </OverviewMonitoringWrapper>
  );
}
